import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { YearCalendar, type YearCalendarEntry } from '../../../../components/calendar/YearCalendar';
import { formatClockTime } from '../../../../lib/formatDate';
import { pressConferencesApi } from '../pressConferenceApi';
import {
  CONFERENCE_STATUS_DOT,
  pressConferencePath,
  type PressConferenceCalendarFilters,
} from '../pressConferenceTypes';

/**
 * The register's CALENDAR view — the shared `<YearCalendar>`, one row per press conference on the
 * day it is held, its dot the conference's state (the tiles above are the legend).
 */
export function PressConferenceYearCalendar({
  filters,
  reloadKey,
}: {
  filters: PressConferenceCalendarFilters;
  reloadKey: number;
}) {
  const navigate = useNavigate();

  // The shell hands a NEW object every render — the fetch keys on what the filters SAY.
  const filterKey = JSON.stringify(filters);

  const load = useCallback(
    (from: string, to: string, signal: AbortSignal) =>
      pressConferencesApi
        .calendar(from, to, JSON.parse(filterKey) as PressConferenceCalendarFilters, signal)
        .then((entries) =>
          entries.map((entry): YearCalendarEntry => {
            const time = formatClockTime(entry.startTime);

            return {
              key: entry.pressConferenceId,
              date: entry.date,
              dot: CONFERENCE_STATUS_DOT[entry.status],
              title: entry.purpose,
              tooltip: `${entry.referenceNumber} — ${entry.purpose} · ${time}, ${entry.venue} · ${entry.statusLabel}`,
              detail: `, ${entry.referenceNumber}, ${time}, ${entry.statusLabel}`,
              onOpen: () => navigate(pressConferencePath(entry.pressConferenceId)),
            };
          }),
        ),
    [filterKey, navigate],
  );

  return (
    <YearCalendar
      id="press-conference-calendar"
      load={load}
      reloadKey={reloadKey}
      summary={(count, year) => `${count} press ${count === 1 ? 'conference' : 'conferences'} in ${year}.`}
    />
  );
}
